/**
 * Shared validation utilities for form inputs
 */

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validates password requirements:
 * - At least 8 characters long
 * - Contains at least one uppercase letter
 * - Contains at least one lowercase letter
 * - Contains at least one digit
 *
 * @param password - The password to validate
 * @returns ValidationError if invalid, null if valid
 */
export function validatePassword(password: string): ValidationError | null {
  if (!password) {
    return { field: 'password', message: 'Password is required' };
  }

  if (password.length < 8) {
    return {
      field: 'password',
      message: 'Password must be at least 8 characters long',
    };
  }

  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    return {
      field: 'password',
      message:
        'Password must contain at least one uppercase letter, one lowercase letter, and one number',
    };
  }

  return null;
}

/**
 * Validates email format
 *
 * @param email - The email to validate
 * @returns ValidationError if invalid, null if valid
 */
export function validateEmail(email: string): ValidationError | null {
  if (!email) {
    return { field: 'email', message: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { field: 'email', message: 'Please enter a valid email address' };
  }

  return null;
}
